// Lightweight tagline A/B tracker.
//
// Goals:
//   - Log every tagline shown ("impression") so we know reach.
//   - Log every click on the tagline chip ("click") so we know intent.
//   - Log a "signup" event the first time a brand-new account is observed,
//     attributing it to whichever tagline that visitor saw most recently.
//
// We keep a stable per-browser session_id in localStorage so a single
// visitor's events stitch together — and so we can dedupe "I saw the same
// tagline 12 times in one session" down to one impression per (session, idx).
//
// All inserts are best-effort and silent on failure: tracking should never
// break the UI or block render.
import { supabase } from "@/integrations/supabase/client";
import { TAGLINES } from "@/lib/taglines";

const SESSION_ID_KEY = "hl:tagline_session_id:v1";
const SEEN_KEY = "hl:tagline_seen_idx:v1";          // impressions deduped per session
const LAST_SHOWN_KEY = "hl:tagline_last_shown:v1";  // index last visible (for signup attribution)
const SIGNUP_ATTRIBUTED_KEY = "hl:tagline_signup_attributed:v1";

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

function readSet(key: string): Set<number> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((n) => typeof n === "number") : []);
  } catch {
    return new Set();
  }
}

function writeSet(key: string, set: Set<number>): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

async function logEvent(
  kind: "impression" | "click" | "signup",
  taglineIdx: number,
  surface: "hero" | "footer" | "",
): Promise<void> {
  if (typeof window === "undefined") return;
  if (taglineIdx < 0 || taglineIdx >= TAGLINES.length) return;
  const sessionId = getSessionId();
  // Best-effort user attribution; signed-out is fine.
  let userId: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    userId = data.session?.user?.id ?? null;
  } catch {
    /* ignore */
  }
  try {
    await supabase.from("tagline_events").insert({
      tagline_idx: taglineIdx,
      tagline_text: TAGLINES[taglineIdx],
      kind,
      session_id: sessionId,
      surface,
      user_id: userId,
      user_agent: navigator.userAgent.slice(0, 500),
      referrer: (document.referrer ?? "").slice(0, 500),
    });
  } catch {
    /* swallow — tracking must never break UX */
  }
}

/** Record an impression. Deduped: each (session, tagline index) only once. */
export function trackTaglineImpression(
  taglineIdx: number,
  surface: "hero" | "footer",
): void {
  if (typeof window === "undefined") return;
  // Always remember "this is the most recent line the user saw" for signup attribution.
  try {
    window.sessionStorage.setItem(LAST_SHOWN_KEY, String(taglineIdx));
  } catch {
    /* ignore */
  }
  const seen = readSet(SEEN_KEY);
  const dedupeKey = taglineIdx * 10 + (surface === "hero" ? 1 : 2);
  if (seen.has(dedupeKey)) return;
  seen.add(dedupeKey);
  writeSet(SEEN_KEY, seen);
  void logEvent("impression", taglineIdx, surface);
}

/** Record a click on the tagline CTA chip. Not deduped. */
export function trackTaglineClick(
  taglineIdx: number,
  surface: "hero" | "footer",
): void {
  void logEvent("click", taglineIdx, surface);
}

/** Read the tagline index that was most recently shown to this session.
 *  Returns null if nothing was shown yet (e.g. user landed straight on a
 *  page that doesn't render the hero/footer tagline).  */
export function getLastShownTaglineIdx(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_SHOWN_KEY);
    if (raw === null) return null;
    const idx = Number(raw);
    return Number.isInteger(idx) && idx >= 0 && idx < TAGLINES.length ? idx : null;
  } catch {
    return null;
  }
}

/** Attribute a signup to the most recently shown tagline. Only fires once
 *  per browser (per session_id) so re-logins don't double-count. */
export function trackTaglineSignup(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(SIGNUP_ATTRIBUTED_KEY) === "1") return;
  } catch {
    /* ignore */
  }
  const idx = getLastShownTaglineIdx();
  // If we never showed the user a tagline (e.g. they arrived on a deep-link
  // that doesn't render the hero/footer), default to index 0 — the canonical
  // line — so the signup still attributes somewhere.
  const attributed = idx ?? 0;
  try {
    window.localStorage.setItem(SIGNUP_ATTRIBUTED_KEY, "1");
  } catch {
    /* ignore */
  }
  void logEvent("signup", attributed, "");
}
